import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import theme from '../UserDashboard/theme';
import { getUserRobots } from '../services/userRobotApi';
import {
  deleteMapRoom,
  fetchMapBlocks,
  fetchMapFloorsByBlock,
  fetchMapFloorsByRobot,
  fetchMapRooms,
  remapMapFloor,
  remapMapFloorWithFiles,
  updateMapBlock,
  updateMapFloorName,
  updateMapRoomName,
} from '../services/userMapApi';

const accent = theme.colors.brandDark;
const accentLight = 'rgba(37, 99, 235, 0.08)';
const PAGE_SIZE = 20;

function apiMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

export function isDisplayableMapSrc(src) {
  if (typeof src !== 'string' || !src.trim()) return false;
  const s = src.trim();
  return (
    s.startsWith('data:') ||
    s.startsWith('http://') ||
    s.startsWith('https://')
  );
}

/** Normalize map_image for <img src> (S3/http URL, data URL, or raw base64). */
function toMapImageSrc(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (isDisplayableMapSrc(s)) return s;
  return `data:image/png;base64,${s}`;
}

function pickMapPreview(item) {
  if (!item) return { src: null, note: '' };
  const image = item.map_image;
  const pgm = item.map_Pgm;
  const imageSrc = toMapImageSrc(image);
  if (imageSrc) return { src: imageSrc, note: '', kind: 'image' };
  const pgmSrc = toMapImageSrc(pgm);
  if (pgmSrc) return { src: pgmSrc, note: '', kind: 'pgm' };
  const note =
    (typeof image === 'string' && image) ||
    (typeof pgm === 'string' && pgm) ||
    'No map image available for this item.';
  return { src: null, note, kind: 'none' };
}

function entityLabel(item) {
  if (!item) return '—';
  return item.roomName || item.floorName || item.blockName || item._id || '—';
}

function mapEntityId(item) {
  if (item == null) return '';
  const id = item._id ?? item.id;
  return id == null ? '' : String(id);
}

function findByEntityId(list, id) {
  const sid = String(id);
  return list.find((item) => mapEntityId(item) === sid);
}

function patchEntityInList(list, id, patch) {
  const sid = String(id);
  return list.map((item) =>
    mapEntityId(item) === sid ? { ...item, ...patch } : item,
  );
}

function ListPanel({ title, items, selectedId, onSelect, getKey, getLabel, emptyText, loading }) {
  return (
    <Box sx={styles.listPanel}>
      <Typography sx={styles.listTitle}>{title}</Typography>
      {loading ? (
        <Box sx={styles.listLoading}>
          <CircularProgress size={28} sx={{ color: accent }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography sx={styles.listEmpty}>{emptyText}</Typography>
      ) : (
        <Box sx={styles.listScroll}>
          {items.map((item) => {
            const id = getKey(item);
            const active = id === selectedId;
            return (
              <Box
                key={id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(item);
                  }
                }}
                sx={{
                  ...styles.listItem,
                  ...(active ? styles.listItemActive : {}),
                }}
              >
                <Typography sx={styles.listItemLabel}>{getLabel(item)}</Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function RenameDialog({ open, title, label, value, onChange, onClose, onSave, saving }) {
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saving || !value.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemapFloorDialog({
  open,
  floorName,
  onFloorNameChange,
  mapImageFile,
  onMapImageChange,
  mapPgmFile,
  onMapPgmChange,
  mapYamlFile,
  onMapYamlChange,
  coordinatesJson,
  onCoordinatesChange,
  onClose,
  onSave,
  saving,
}) {
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Remap floor</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>
          Upload new map files to S3. Leave fields empty to keep existing assets.
        </Typography>
        <TextField
          label="Floor name"
          value={floorName}
          onChange={(e) => onFloorNameChange(e.target.value)}
          fullWidth
          disabled={saving}
        />
        <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={saving}>
          {mapImageFile ? mapImageFile.name : 'Map image (PNG)'}
          <input
            type="file"
            hidden
            accept="image/png,image/*"
            onChange={(e) => onMapImageChange(e.target.files?.[0] || null)}
          />
        </Button>
        <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={saving}>
          {mapPgmFile ? mapPgmFile.name : 'Map PGM'}
          <input type="file" hidden onChange={(e) => onMapPgmChange(e.target.files?.[0] || null)} />
        </Button>
        <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={saving}>
          {mapYamlFile ? mapYamlFile.name : 'Map YAML'}
          <input
            type="file"
            hidden
            accept=".yaml,.yml,text/yaml,application/x-yaml"
            onChange={(e) => onMapYamlChange(e.target.files?.[0] || null)}
          />
        </Button>
        <TextField
          label="Coordinates (JSON array)"
          value={coordinatesJson}
          onChange={(e) => onCoordinatesChange(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          placeholder='[[1.2, 3.4], [5.6, 7.8]] or [] to clear'
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Uploading…' : 'Remap floor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MapPreview({ item }) {
  const { src, note } = pickMapPreview(item);
  const name = entityLabel(item);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [src, item?._id]);

  return (
    <Box sx={styles.previewWrap}>
      <Typography sx={styles.previewTitle}>{name}</Typography>
      {src && !imageError ? (
        <Box
          component="img"
          src={src}
          alt={`Map for ${name}`}
          sx={styles.previewImage}
          onError={() => setImageError(true)}
        />
      ) : (
        <Box sx={styles.previewPlaceholder}>
          <MapOutlinedIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={styles.previewNote}>
            {imageError ? 'Could not load map image from URL.' : note}
          </Typography>
        </Box>
      )}
      {item?.coordinates?.length > 0 && (
        <Typography sx={styles.coordHint}>
          {item.coordinates.length} coordinate region
          {item.coordinates.length === 1 ? '' : 's'} defined
        </Typography>
      )}
    </Box>
  );
}

/**
 * Block → Floor → Room map explorer for one robot.
 * @param {{ robotId?: string, compact?: boolean, onBack?: () => void }} props
 */
export function UserMapExplorer({ robotId: robotIdProp, compact = false, onBack }) {
  const [robots, setRobots] = useState([]);
  const [robotId, setRobotId] = useState(robotIdProp || '');
  const [noBlock, setNoBlock] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loadingRobots, setLoadingRobots] = useState(!robotIdProp);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [remapOpen, setRemapOpen] = useState(false);
  const [remapFloorName, setRemapFloorName] = useState('');
  const [mapImageFile, setMapImageFile] = useState(null);
  const [mapPgmFile, setMapPgmFile] = useState(null);
  const [mapYamlFile, setMapYamlFile] = useState(null);
  const [coordinatesJson, setCoordinatesJson] = useState('');
  const [savingRemap, setSavingRemap] = useState(false);

  const effectiveRobotId = robotIdProp || robotId;

  useEffect(() => {
    if (robotIdProp) setRobotId(robotIdProp);
  }, [robotIdProp]);

  useEffect(() => {
    if (robotIdProp) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingRobots(true);
      try {
        const res = await getUserRobots();
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        if (!cancelled) {
          setRobots(list);
          if (!robotId && list[0]?.robotId) setRobotId(list[0].robotId);
        }
      } catch (err) {
        if (!cancelled) setError(apiMessage(err, 'Failed to load robots.'));
      } finally {
        if (!cancelled) setLoadingRobots(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [robotIdProp, robotId]);

  const resetSelection = useCallback(() => {
    setSelectedBlock(null);
    setSelectedFloor(null);
    setSelectedRoom(null);
    setBlocks([]);
    setFloors([]);
    setRooms([]);
  }, []);

  const loadFloorsForRobot = useCallback(async (rid, embeddedFloors) => {
    if (embeddedFloors?.length) {
      setFloors(embeddedFloors);
      setSelectedFloor(embeddedFloors[0]);
      return embeddedFloors[0];
    }
    setLoadingFloors(true);
    try {
      const res = await fetchMapFloorsByRobot(rid, { page: 1, limit: PAGE_SIZE });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setFloors(list);
      const first = list[0] || null;
      setSelectedFloor(first);
      return first;
    } catch (err) {
      setFloors([]);
      setSelectedFloor(null);
      setError(apiMessage(err, 'Could not load floors.'));
      return null;
    } finally {
      setLoadingFloors(false);
    }
  }, []);

  const loadTree = useCallback(async () => {
    if (!effectiveRobotId) return;
    setLoadingTree(true);
    setError('');
    resetSelection();
    try {
      const res = await fetchMapBlocks(effectiveRobotId, {
        page: 1,
        limit: PAGE_SIZE,
        floorLimit: 50,
      });
      const body = res.data || {};
      const isNoBlock = Boolean(body.noBlock);
      const blockList = Array.isArray(body.data) ? body.data : [];
      setNoBlock(isNoBlock);
      setBlocks(blockList);

      if (isNoBlock) {
        const embedded = Array.isArray(body.floors) ? body.floors : [];
        await loadFloorsForRobot(effectiveRobotId, embedded);
      } else if (blockList.length > 0) {
        const first = blockList[0];
        setSelectedBlock(first);
        setLoadingFloors(true);
        try {
          const floorsRes = await fetchMapFloorsByBlock(first._id, {
            page: 1,
            limit: PAGE_SIZE,
          });
          const fl = Array.isArray(floorsRes.data?.data) ? floorsRes.data.data : [];
          setFloors(fl);
          setSelectedFloor(fl[0] || null);
        } catch (err) {
          setError(apiMessage(err, 'Could not load floors.'));
        } finally {
          setLoadingFloors(false);
        }
      }
    } catch (err) {
      setError(apiMessage(err, 'Could not load map blocks.'));
    } finally {
      setLoadingTree(false);
    }
  }, [effectiveRobotId, loadFloorsForRobot, resetSelection]);

  useEffect(() => {
    if (!effectiveRobotId) return;
    loadTree();
  }, [effectiveRobotId, loadTree]);

  const loadRooms = useCallback(async (floor) => {
    if (!floor?._id) {
      setRooms([]);
      setSelectedRoom(null);
      return;
    }
    setLoadingRooms(true);
    try {
      const res = await fetchMapRooms(floor._id, { page: 1, limit: PAGE_SIZE });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRooms(list);
      setSelectedRoom(null);
    } catch (err) {
      setRooms([]);
      setError(apiMessage(err, 'Could not load rooms.'));
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFloor?._id) loadRooms(selectedFloor);
    else {
      setRooms([]);
      setSelectedRoom(null);
    }
  }, [selectedFloor?._id, loadRooms, selectedFloor]);

  const selectedFloorId = mapEntityId(selectedFloor);

  const refreshFloors = useCallback(async () => {
    try {
      let list = [];
      if (noBlock) {
        const res = await fetchMapFloorsByRobot(effectiveRobotId, {
          page: 1,
          limit: PAGE_SIZE,
        });
        list = Array.isArray(res.data?.data) ? res.data.data : [];
      } else if (selectedBlock?._id) {
        const res = await fetchMapFloorsByBlock(selectedBlock._id, {
          page: 1,
          limit: PAGE_SIZE,
        });
        list = Array.isArray(res.data?.data) ? res.data.data : [];
      }
      setFloors(list);
      const next = findByEntityId(list, selectedFloorId) || list[0] || null;
      setSelectedFloor(next);
      return next;
    } catch (err) {
      setError(apiMessage(err, 'Could not refresh floors.'));
      return null;
    }
  }, [effectiveRobotId, noBlock, selectedBlock?._id, selectedFloorId]);

  const openRename = (type, item) => {
    const name =
      type === 'block'
        ? item.blockName
        : type === 'floor'
          ? item.floorName
          : item.roomName;
    setRenameTarget({ type, id: item._id, name: name || '' });
    setRenameValue(name || '');
  };

  const closeRename = () => {
    if (savingRename) return;
    setRenameTarget(null);
    setRenameValue('');
  };

  const handleSaveRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setSavingRename(true);
    try {
      let res;
      const trimmed = renameValue.trim();
      if (renameTarget.type === 'block') {
        res = await updateMapBlock(renameTarget.id, { blockName: trimmed });
        const updated = res.data?.data;
        const patch = { blockName: updated?.blockName ?? trimmed };
        setBlocks((prev) => patchEntityInList(prev, renameTarget.id, patch));
        setSelectedBlock((prev) =>
          prev && mapEntityId(prev) === String(renameTarget.id)
            ? { ...prev, ...patch }
            : prev,
        );
      } else if (renameTarget.type === 'floor') {
        res = await updateMapFloorName(renameTarget.id, trimmed);
        const updated = res.data?.data;
        const patch = { floorName: updated?.floorName ?? trimmed };
        setFloors((prev) => patchEntityInList(prev, renameTarget.id, patch));
        setSelectedFloor((prev) =>
          prev && mapEntityId(prev) === String(renameTarget.id)
            ? { ...prev, ...patch }
            : prev,
        );
      } else {
        res = await updateMapRoomName(renameTarget.id, trimmed);
        const updated = res.data?.data;
        const patch = { roomName: updated?.roomName ?? trimmed };
        setRooms((prev) => patchEntityInList(prev, renameTarget.id, patch));
        setSelectedRoom((prev) =>
          prev && mapEntityId(prev) === String(renameTarget.id)
            ? { ...prev, ...patch }
            : prev,
        );
      }
      setSnackbar({
        open: true,
        message: res.data?.message || 'Updated successfully.',
        severity: 'success',
      });
      setRenameTarget(null);
      setRenameValue('');
    } catch (err) {
      setSnackbar({
        open: true,
        message: apiMessage(err, 'Update failed.'),
        severity: 'error',
      });
    } finally {
      setSavingRename(false);
    }
  };

  const openRemapDialog = () => {
    if (!selectedFloor) return;
    setRemapFloorName(selectedFloor.floorName || '');
    setMapImageFile(null);
    setMapPgmFile(null);
    setMapYamlFile(null);
    setCoordinatesJson(
      Array.isArray(selectedFloor.coordinates)
        ? JSON.stringify(selectedFloor.coordinates)
        : '',
    );
    setRemapOpen(true);
  };

  const closeRemapDialog = () => {
    if (savingRemap) return;
    setRemapOpen(false);
  };

  const handleSaveRemap = async () => {
    if (!selectedFloor?._id) return;

    const hasFiles = mapImageFile || mapPgmFile || mapYamlFile;
    const coordsTrim = coordinatesJson.trim();
    const floorNameTrim = remapFloorName.trim();
    const hasFloorNameChange =
      floorNameTrim && floorNameTrim !== (selectedFloor.floorName || '');

    if (!hasFiles && !coordsTrim && !hasFloorNameChange) {
      setSnackbar({
        open: true,
        message: 'Provide at least one field to update.',
        severity: 'error',
      });
      return;
    }

    setSavingRemap(true);
    try {
      let res;
      if (hasFiles) {
        const formData = new FormData();
        formData.append('floorId', selectedFloor._id);
        if (floorNameTrim) formData.append('floorName', floorNameTrim);
        if (mapImageFile) formData.append('map_image', mapImageFile);
        if (mapPgmFile) formData.append('map_Pgm', mapPgmFile);
        if (mapYamlFile) formData.append('map_Yaml', mapYamlFile);
        if (coordsTrim) formData.append('coordinates', coordsTrim);
        res = await remapMapFloorWithFiles(formData);
      } else {
        const payload = { floorId: selectedFloor._id };
        if (hasFloorNameChange) payload.floorName = floorNameTrim;
        if (coordsTrim) {
          payload.coordinates = JSON.parse(coordsTrim);
        }
        res = await remapMapFloor(payload);
      }
      setSnackbar({
        open: true,
        message: res.data?.message || 'Floor remapped successfully.',
        severity: 'success',
      });
      closeRemapDialog();
      await refreshFloors();
    } catch (err) {
      const msg =
        err instanceof SyntaxError
          ? 'Coordinates must be valid JSON.'
          : apiMessage(err, 'Remap failed.');
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setSavingRemap(false);
    }
  };

  const handleSelectBlock = async (block) => {
    setSelectedBlock(block);
    setSelectedFloor(null);
    setSelectedRoom(null);
    setRooms([]);
    setLoadingFloors(true);
    try {
      const res = await fetchMapFloorsByBlock(block._id, { page: 1, limit: PAGE_SIZE });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setFloors(list);
      setSelectedFloor(list[0] || null);
    } catch (err) {
      setFloors([]);
      setError(apiMessage(err, 'Could not load floors.'));
    } finally {
      setLoadingFloors(false);
    }
  };

  const handleSelectFloor = (floor) => {
    setSelectedFloor(floor);
    setSelectedRoom(null);
  };

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
  };

  const previewItem = selectedRoom || selectedFloor;
  const showBlocks = !noBlock && blocks.length > 0;

  const breadcrumb = useMemo(() => {
    const parts = [{ label: effectiveRobotId || 'Robot', key: 'robot' }];
    if (showBlocks && selectedBlock) {
      parts.push({ label: selectedBlock.blockName || 'Block', key: 'block' });
    }
    if (selectedFloor) {
      parts.push({ label: selectedFloor.floorName || 'Floor', key: 'floor' });
    }
    if (selectedRoom) {
      parts.push({ label: selectedRoom.roomName || 'Room', key: 'room' });
    }
    return parts;
  }, [effectiveRobotId, showBlocks, selectedBlock, selectedFloor, selectedRoom]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !selectedFloor?._id) return;
    setDeleting(true);
    try {
      const res = await deleteMapRoom(selectedFloor._id, deleteTarget.roomName);
      setSnackbar({
        open: true,
        message: res.data?.message || 'Room deleted successfully.',
        severity: 'success',
      });
      setDeleteTarget(null);
      await loadRooms(selectedFloor);
    } catch (err) {
      setSnackbar({
        open: true,
        message: apiMessage(err, 'Failed to delete room.'),
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const pageLoading = loadingRobots || loadingTree;

  return (
    <Box sx={compact ? styles.embedWrap : styles.pageWrap}>
      {!compact && (
        <Box sx={styles.header}>
          {onBack && (
            <IconButton onClick={onBack} size="small" sx={{ mr: 1, alignSelf: 'flex-start' }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box>
            <Typography sx={styles.title}>Robot maps</Typography>
            <Typography sx={styles.subtitle}>
              View and update blocks, floors, and rooms. Remap floors to upload new map files to S3.
            </Typography>
          </Box>
        </Box>
      )}

      {!robotIdProp && (
        <FormControl size="small" sx={styles.robotSelect} disabled={loadingRobots}>
          <InputLabel id="map-robot-label">Robot</InputLabel>
          <Select
            labelId="map-robot-label"
            label="Robot"
            value={robotId}
            onChange={(e) => setRobotId(e.target.value)}
          >
            {robots.map((r) => (
              <MenuItem key={r.robotId} value={r.robotId}>
                {r.username ? `${r.username} (${r.robotId})` : r.robotId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {noBlock && !loadingTree && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          This robot uses a direct floor layout (no named blocks).
        </Alert>
      )}

      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        {breadcrumb.map((b, i) => (
          <Typography
            key={b.key}
            sx={{
              fontSize: 13,
              fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
              color:
                i === breadcrumb.length - 1
                  ? theme.colors.textPrimary
                  : theme.colors.textSecondary,
            }}
          >
            {b.label}
          </Typography>
        ))}
      </Breadcrumbs>

      {pageLoading ? (
        <Box sx={styles.centered}>
          <CircularProgress sx={{ color: accent }} />
        </Box>
      ) : !effectiveRobotId ? (
        <Typography sx={styles.listEmpty}>Select a robot to view maps.</Typography>
      ) : (
        <Box sx={styles.mainGrid}>
          <Box sx={styles.columns}>
            {showBlocks && (
              <ListPanel
                title="Blocks"
                items={blocks}
                selectedId={selectedBlock?._id}
                onSelect={handleSelectBlock}
                getKey={(b) => b._id}
                getLabel={(b) => b.blockName || b._id}
                emptyText="No blocks configured."
                loading={loadingTree}
              />
            )}
            <ListPanel
              title="Floors"
              items={floors}
              selectedId={selectedFloor?._id}
              onSelect={handleSelectFloor}
              getKey={(f) => f._id}
              getLabel={(f) => f.floorName || f._id}
              emptyText={noBlock ? 'No floors for this robot.' : 'Select a block or add floors.'}
              loading={loadingFloors}
            />
            <ListPanel
              title="Rooms"
              items={rooms}
              selectedId={selectedRoom?._id}
              onSelect={handleSelectRoom}
              getKey={(r) => r._id || r.roomName}
              getLabel={(r) => r.roomName || r._id}
              emptyText={selectedFloor ? 'No rooms on this floor.' : 'Select a floor.'}
              loading={loadingRooms}
            />
          </Box>

          <Box sx={styles.previewColumn}>
            {previewItem ? (
              <>
                <MapPreview item={previewItem} />
                <Box sx={styles.actionRow}>
                  {showBlocks && selectedBlock && !selectedBlock.noBlock && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => openRename('block', selectedBlock)}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Rename block
                    </Button>
                  )}
                  {selectedFloor && !selectedRoom && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => openRename('floor', selectedFloor)}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                      >
                        Rename floor
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileOutlinedIcon />}
                        onClick={openRemapDialog}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                      >
                        Remap floor
                      </Button>
                    </>
                  )}
                  {selectedRoom && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => openRename('room', selectedRoom)}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Rename room
                    </Button>
                  )}
                </Box>
                {selectedRoom && selectedFloor && (
                  <Box sx={styles.deleteRow}>
                    <Button
                      color="error"
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setDeleteTarget(selectedRoom)}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Delete room
                    </Button>
                    <Typography sx={styles.deleteHint}>
                      Deletes &quot;{selectedRoom.roomName}&quot; on this floor (exact name match).
                    </Typography>
                  </Box>
                )}
                {!selectedRoom && selectedFloor && rooms.length > 0 && (
                  <Typography sx={styles.hint}>
                    Select a room to view its map or delete it.
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={styles.previewPlaceholder}>
                <MapOutlinedIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={styles.previewNote}>
                  {floors.length === 0
                    ? 'No map data for this robot yet.'
                    : 'Select a floor to preview its map.'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete room?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
            Remove room <strong>{deleteTarget?.roomName}</strong> from floor{' '}
            <strong>{selectedFloor?.floorName || selectedFloor?._id}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <RenameDialog
        open={Boolean(renameTarget)}
        title={
          renameTarget?.type === 'block'
            ? 'Rename block'
            : renameTarget?.type === 'floor'
              ? 'Rename floor'
              : 'Rename room'
        }
        label={
          renameTarget?.type === 'block'
            ? 'Block name'
            : renameTarget?.type === 'floor'
              ? 'Floor name'
              : 'Room name'
        }
        value={renameValue}
        onChange={setRenameValue}
        onClose={closeRename}
        onSave={handleSaveRename}
        saving={savingRename}
      />

      <RemapFloorDialog
        open={remapOpen}
        floorName={remapFloorName}
        onFloorNameChange={setRemapFloorName}
        mapImageFile={mapImageFile}
        onMapImageChange={setMapImageFile}
        mapPgmFile={mapPgmFile}
        onMapPgmChange={setMapPgmFile}
        mapYamlFile={mapYamlFile}
        onMapYamlChange={setMapYamlFile}
        coordinatesJson={coordinatesJson}
        onCoordinatesChange={setCoordinatesJson}
        onClose={closeRemapDialog}
        onSave={handleSaveRemap}
        saving={savingRemap}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/** Standalone page at /user-app/maps */
export default function UserMapPage() {
  return <UserMapExplorer />;
}

const styles = {
  pageWrap: {
    width: '100%',
    maxWidth: 1200,
    mx: 'auto',
    py: { xs: 2, md: 3 },
    px: { xs: 2, md: 3 },
  },
  embedWrap: {
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    mb: 2,
  },
  title: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: theme.colors.textPrimary,
  },
  subtitle: {
    fontSize: '0.9rem',
    color: theme.colors.textSecondary,
    mt: 0.5,
    maxWidth: 560,
  },
  robotSelect: {
    minWidth: 280,
    mb: 2,
    bgcolor: theme.colors.bgPrimary,
  },
  mainGrid: {
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    gap: 2,
    alignItems: 'stretch',
  },
  columns: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 1.5,
    flex: { lg: '0 0 auto' },
    minWidth: 0,
  },
  listPanel: {
    width: { xs: '100%', md: 180 },
    flexShrink: 0,
    borderRadius: 2,
    border: `1px solid ${theme.colors.borderDefault}`,
    bgcolor: theme.colors.bgPrimary,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: { xs: 200, md: 'calc(100vh - 320px)' },
  },
  listTitle: {
    px: 1.5,
    py: 1,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textSecondary,
    borderBottom: `1px solid ${theme.colors.borderDefault}`,
    bgcolor: theme.colors.bgSecondary,
  },
  listScroll: {
    overflow: 'auto',
    flex: 1,
  },
  listItem: {
    px: 1.5,
    py: 1.25,
    cursor: 'pointer',
    borderBottom: `1px solid ${theme.colors.borderDefault}`,
    '&:hover': { bgcolor: accentLight },
  },
  listItemActive: {
    bgcolor: accentLight,
    borderLeft: `3px solid ${accent}`,
    pl: 'calc(12px - 3px)',
  },
  listItemLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.textPrimary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listEmpty: {
    px: 1.5,
    py: 2,
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  listLoading: {
    display: 'flex',
    justifyContent: 'center',
    py: 3,
  },
  previewColumn: {
    flex: 1,
    minWidth: 0,
    borderRadius: 2,
    border: `1px solid ${theme.colors.borderDefault}`,
    bgcolor: theme.colors.bgPrimary,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    minHeight: 280,
  },
  previewWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.colors.textPrimary,
    mb: 1,
  },
  previewImage: {
    width: '100%',
    maxHeight: 'min(60vh, 480px)',
    objectFit: 'contain',
    bgcolor: theme.colors.bgTertiary,
    borderRadius: 2,
    border: `1px solid ${theme.colors.borderDefault}`,
  },
  previewPlaceholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: theme.colors.bgSecondary,
    borderRadius: 2,
    p: 3,
    minHeight: 200,
  },
  previewNote: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  coordHint: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    mt: 1,
  },
  hint: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    pt: 1,
    borderTop: `1px solid ${theme.colors.borderDefault}`,
  },
  deleteRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.5,
    pt: 1,
    borderTop: `1px solid ${theme.colors.borderDefault}`,
  },
  deleteHint: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    py: 6,
  },
};
